import { useCallback, useEffect, useRef } from 'react';
import { millisecondsInSecond } from 'date-fns/constants';

import { usePostHeartBeatMutation } from 'src/common/slices/heartbeat';
import { ROOM_HEART_BEAT_INTERVAL_SECONDS } from 'src/features/room/room.constants';

export const useRoomHeartBeat = (
  roomId: string | undefined,
  isUserLoggedIn: boolean,
  isRoomCompleted: boolean | undefined,
  interval = ROOM_HEART_BEAT_INTERVAL_SECONDS,
) => {
  const [postHeartbeatMutation] = usePostHeartBeatMutation();
  const timerRef = useRef<NodeJS.Timer>();
  const sendHeartbeat = useCallback(async () => {
    // base 64 encoding
    const payload = btoa(
      JSON.stringify({
        value: interval,
        timestamp: Date.now(),
        roomId,
      }),
    );

    try {
      await postHeartbeatMutation({ payload });
      // eslint-disable-next-line no-empty
    } catch {}
  }, [postHeartbeatMutation, interval, roomId]);

  useEffect(() => {
    if (!isUserLoggedIn || !roomId || isRoomCompleted) {
      timerRef.current = undefined;
      return;
    }

    timerRef.current = setInterval(() => {
      // eslint-disable-next-line no-void
      void (async () => {
        if (document.hasFocus() && !isRoomCompleted) {
          await sendHeartbeat();
        }
      })();
    }, interval * millisecondsInSecond);
    // eslint-disable-next-line consistent-return
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = undefined;
      }
    };
  }, [sendHeartbeat, isRoomCompleted, isUserLoggedIn, roomId, interval]);
  return { sendHeartbeat, timerRef };
};
